import { Component, OnInit, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, UntypedFormControl, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { SelectBox } from 'cbc-shared-lib';

import {
  Szza020aResponse
} from 'sg-api';

import { SgKdConstants } from '../../../../../sg-kd-shared/sg-kd-constants';

import { UsedSgkd002dSzza020aService } from '../../services/used-sgkd002d-szza020a.service';

@Component({
  selector: 'app-sgkd002d-kouza',
  templateUrl: './sgkd002d-kouza.component.html',
  styleUrl: './sgkd002d-kouza.component.scss'
})
export class Sgkd002dKouzaComponent implements OnInit, OnDestroy {

  private onDestroy$ = new Subject();

  /** メインフォーム */
  @Input() mainForm: UntypedFormGroup;
  /** 預金種別コードリスト */
  @Input() codeDptClsfyCdList: SelectBox[];
  /** 受取方法コードリスト */
  @Input() codeRecivMethodList: SelectBox[];
  /** 業務日付 */
  @Input() businessDate: string;

  /** 口座振替フォーム */
  kouzaForm: UntypedFormGroup;

  /** レスポンス */
  response: Szza020aResponse;

  /** メッセージクリア通知イベント */
  @Output() clearMessageEvent = new EventEmitter();

  constructor(
    private formBuilder: UntypedFormBuilder,
    private usedSgkd002dSzza020aService: UsedSgkd002dSzza020aService) {
  }

  /**
   * 初期処理
   * 
   * @memberof Sgkd002dKouzaComponent
   */
  ngOnInit(): void {
    this.kouzaForm = this.createKouzaForm();
    this.mainForm.addControl("kouzaForm", this.kouzaForm);
  }

  /**
   * 破棄処理
   * 
   * @memberof Sgkd002dKouzaComponent
   */
  ngOnDestroy(): void {
    this.mainForm.removeControl("kouzaForm");
    this.onDestroy$.next();
  }

  /**
   * 口座振替フォームの生成
   * 
   * @returns - 口座振替フォーム
   * @memberof Sgkd002dKouzaComponent
   */
  private createKouzaForm(): UntypedFormGroup {
    return this.formBuilder.group({
      // 口座名義（漢字）
      kohuriActknj: new UntypedFormControl(
        { value: '', disabled: false },
        [ Validators.required, Validators.maxLength(SgKdConstants.SGKD002D.MAX_LENGTH.KOUHURI_ACT_KNJ) ]
      ),
      // 口座名義（カナ）
      meigiKana: new UntypedFormControl(
        { value: '', disabled: false },
        [
          Validators.required,
          Validators.maxLength(SgKdConstants.SGKD002D.MAX_LENGTH.MEIGI_KANA),
          Validators.pattern(SgKdConstants.PATTERN.ZEN_KANA)
        ]
      ),
      // 金融機関コード
      extBankfcCode: new UntypedFormControl(
        { value: '', disabled: false },
        [
          Validators.required,
          Validators.minLength(SgKdConstants.SGKD002D.MAX_LENGTH.EXT_BANKFC_CODE),
          Validators.maxLength(SgKdConstants.SGKD002D.MAX_LENGTH.EXT_BANKFC_CODE),
          Validators.pattern(SgKdConstants.PATTERN.HAN_NUM)
        ]
      ),
      // 支店コード
      extBranchCode: new UntypedFormControl(
        { value: '', disabled: false },
        [
          Validators.required,
          Validators.minLength(SgKdConstants.SGKD002D.MAX_LENGTH.EXT_BRANCH_CODE),
          Validators.maxLength(SgKdConstants.SGKD002D.MAX_LENGTH.EXT_BRANCH_CODE),
          Validators.pattern(SgKdConstants.PATTERN.HAN_NUM)
        ]
      ),
      // 金融機関名
      bankfcAname: new UntypedFormControl({ value: '', disabled: true }, Validators.required),
      // 支店名
      branchAname: new UntypedFormControl({ value: '', disabled: true }, Validators.required),
      // 預金種別コード
      dptClsfy: new UntypedFormControl(
        { value: '', disabled: false },
        [ Validators.required ]
      ),
      // 口座番号
      kouzaNo: new UntypedFormControl(
        { value: '', disabled: false },
        [
          Validators.required,
          Validators.minLength(SgKdConstants.SGKD002D.MAX_LENGTH.KOUZA_NO),
          Validators.maxLength(SgKdConstants.SGKD002D.MAX_LENGTH.KOUZA_NO),
          Validators.pattern(SgKdConstants.PATTERN.HAN_NUM)
        ]
      ),
      // 受取方法区分
      recivWayDivcd: new UntypedFormControl(
        { value: '', disabled: false },
        [ Validators.required ]
      ),
    });
  }

  /**
   * 金融機関名検索
   * 
   * @memberof Sgkd002dKouzaComponent
   */
  searchBankNm(): void {

    this.clearMessageEvent.emit();

    this.kouzaForm.get('bankfcAname').setValue(SgKdConstants.STR.EMPTY);
    this.kouzaForm.get('branchAname').setValue(SgKdConstants.STR.EMPTY);

    if (this.kouzaForm.get('extBankfcCode')?.value.length != SgKdConstants.SGKD002D.MAX_LENGTH.EXT_BANKFC_CODE) {
      return;
    }

    this.usedSgkd002dSzza020aService.getBankInfo(
      this.kouzaForm.get('extBankfcCode')?.value, null, this.businessDate)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(
        response => {
          this.kouzaForm.get('bankfcAname').setValue(response?.bankMasterSrchResultList[0]?.bankfcAname);
        }, error => {
        }
      );
  }

  /**
   * 支店名検索
   * 
   * @memberof Sgkd002dKouzaComponent
   */
  searchBranchNm(): void {

    this.kouzaForm.get('branchAname').setValue(SgKdConstants.STR.EMPTY);

    if (this.kouzaForm.get('extBankfcCode')?.value.length != SgKdConstants.SGKD002D.MAX_LENGTH.EXT_BANKFC_CODE) {
      return;
    }

    if (this.kouzaForm.get('extBranchCode')?.value.length != SgKdConstants.SGKD002D.MAX_LENGTH.EXT_BRANCH_CODE) {
      return;
    }

    this.usedSgkd002dSzza020aService.getBankInfo(
      this.kouzaForm.get('extBankfcCode')?.value, this.kouzaForm.get('extBranchCode')?.value, this.businessDate)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(
        response => {
          this.kouzaForm.get('branchAname').setValue(response?.bankMasterSrchResultList[0]?.branchAname);
        }, error => {
        }
      );
  }
}
